import React, { useState, useMemo } from 'react';
import { PlusIcon, BuildingOffice2Icon, MagnifyingGlassIcon, FunnelIcon, EyeIcon, PencilIcon, TrashIcon, UserIcon, CalendarDaysIcon, DocumentTextIcon, ChevronUpDownIcon, MapPinIcon, PhoneIcon, EnvelopeIcon, IdentificationIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { Combobox } from '@headlessui/react';
import { useCustomers } from '../hooks/useCustomers';
import { useEnergyContracts, EnergyContract } from '../hooks/useEnergyContracts';
import { Customer } from '../types/customer';
import Modal from '../components/common/Modal';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import SearchBar from '../components/common/SearchBar';

// EnergyContract interface importálva a hook-ból

interface EnergyContractFormData {
  customerId: string;
  selectedPods: string[];
  contractNumber?: string; // Opcionális manuális szerződésszám
  contractType: 'new' | 'modification' | 'renewal' | 'termination' | 'live';
  provider: 'EON' | 'ELMU' | 'DEMASZ' | 'OPUS' | 'MVMI';
  tariffType: 'residential' | 'commercial' | 'industrial';
  status: 'draft' | 'active' | 'pending' | 'expired' | 'terminated';
  startDate: string;
  endDate: string;
  currency: 'HUF' | 'EUR';
  pricePerKwh: number;
  monthlyFee: number;
  pricePackage: string;
  notes: string;
}

const EnergiaContracts = () => {
  const { t } = useLanguage();
  const { customers } = useCustomers();
  const { contracts, loading, error, addContract, updateContract, deleteContract } = useEnergyContracts();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedContracts, setSelectedContracts] = useState<string[]>([]);
  const [showContractForm, setShowContractForm] = useState(false);
  const [selectedContract, setSelectedContract] = useState<EnergyContract | null>(null);
  
  // Szűrők
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [contractTypeFilter, setContractTypeFilter] = useState<string>('');
  const [tariffTypeFilter, setTariffTypeFilter] = useState<string>('');
  const [customerFilter, setCustomerFilter] = useState<string>('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Szűrt szerződések
  const filteredContracts = useMemo(() => {
    return contracts.filter(contract => {
      const matchesSearch = contract.contractNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           contract.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           contract.pods.some(pod => pod.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                    pod.address.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = !statusFilter || contract.status === statusFilter;
      const matchesProvider = !providerFilter || contract.provider === providerFilter;
      const matchesContractType = !contractTypeFilter || contract.contractType === contractTypeFilter;
      const matchesTariffType = !tariffTypeFilter || contract.tariffType === tariffTypeFilter;
      const matchesCustomer = !customerFilter || contract.customerId === customerFilter;
      
      return matchesSearch && matchesStatus && matchesProvider && matchesContractType && matchesTariffType && matchesCustomer;
    });
  }, [contracts, searchTerm, statusFilter, providerFilter, contractTypeFilter, tariffTypeFilter, customerFilter]);

  // Csak azok az ügyfelek, akiknek van POD adatuk
  const customersWithPods = useMemo(() => {
    return customers.filter(customer => 
      (customer.pod?.id) || 
      (customer.additionalPods && customer.additionalPods.length > 0)
    );
  }, [customers]);

  // Pagination számítás
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentContracts = filteredContracts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);

  // Státusz színek
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'terminated': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  // Státusz szöveg
  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktív';
      case 'pending': return 'Függőben';
      case 'expired': return 'Lejárt';
      case 'terminated': return 'Felmondva';
      default: return 'Piszkozat';
    }
  };

  // Szolgáltató színek
  const getProviderColor = (provider: string) => {
    switch (provider) {
      case 'EON': return 'bg-blue-100 text-blue-800';
      case 'ELMU': return 'bg-green-100 text-green-800';
      case 'DEMASZ': return 'bg-purple-100 text-purple-800';
      case 'OPUS': return 'bg-orange-100 text-orange-800';
      case 'MVMI': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Új szerződés hozzáadása
  const handleCreateContract = async (data: EnergyContractFormData) => {
    const customer = customers.find(c => c.id === data.customerId);
    if (!customer) {
      toast.error('Ügyfél nem található!');
      return;
    }

    if (data.selectedPods.length === 0) {
      toast.error('Legalább egy POD kiválasztása kötelező!');
      return;
    }

    // Egy szerződés létrehozása az összes kiválasztott POD-dal
    const selectedPodsData = [];
    
    for (const podId of data.selectedPods) {
      // POD adatok megkeresése
      let selectedPod;
      if (customer.pod?.id === podId) {
        selectedPod = customer.pod;
      } else if (customer.additionalPods) {
        selectedPod = customer.additionalPods.find(pod => pod.id === podId);
      }

      if (!selectedPod) {
        toast.error(`POD nem található: ${podId}`);
        return;
      }

      selectedPodsData.push({
        id: selectedPod.id,
        address: selectedPod.address,
        measurementType: selectedPod.measurementType,
        annualConsumption: selectedPod.annualConsumption
      });
    }

    // Szerződésszám - manuális vagy generált
    let contractNumber = data.contractNumber;
    if (!contractNumber || contractNumber.trim() === '') {
      // Automatikus generálás, ha nincs megadva
      const year = new Date().getFullYear();
      const month = String(new Date().getMonth() + 1).padStart(2, '0');
      const customerCode = customer.customerCode || '0001';
      contractNumber = `EI-${customerCode}-S-${year}${month}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
    }

    const contractData = {
      contractNumber,
      customerId: customer.id,
      customerName: customer.type === 'organization' ? customer.companyName : `${customer.firstName} ${customer.lastName}`,
      customerType: customer.type,
      pods: selectedPodsData,
      contractType: data.contractType,
      provider: data.provider,
      tariffType: data.tariffType,
      status: data.status,
      startDate: data.startDate,
      endDate: data.endDate,
      currency: data.currency,
      pricePerKwh: data.pricePerKwh,
      monthlyFee: data.monthlyFee,
      pricePackage: data.pricePackage,
      capacity: parseFloat(customer.consumptionData?.power || '0'),
      profileNumber: customer.consumptionData?.profileNumber || '',
      notes: data.notes,
      createdBy: 'current-user', // TODO: aktuális felhasználó
    };
    
    try {
      await addContract(contractData);
      setShowContractForm(false);
      toast.success(`Szerződés sikeresen létrehozva ${selectedPodsData.length} POD-dal!`);
    } catch (err) {
      toast.error('Hiba a szerződés mentése során!');
    }
  };

  // Szerződés szerkesztése
  const handleEditContract = async (data: EnergyContractFormData) => {
    if (!selectedContract) return;
    
    const customer = customers.find(c => c.id === data.customerId);
    if (!customer) {
      toast.error('Ügyfél nem található!');
      return;
    }

    if (data.selectedPods.length === 0) {
      toast.error('Legalább egy POD kiválasztása kötelező!');
      return;
    }

    // Az összes kiválasztott POD feldolgozása szerkesztésnél
    const selectedPodsData = [];
    
    for (const podId of data.selectedPods) {
      let selectedPod;
      if (customer.pod?.id === podId) {
        selectedPod = customer.pod;
      } else if (customer.additionalPods) {
        selectedPod = customer.additionalPods.find(pod => pod.id === podId);
      }

      if (!selectedPod) {
        toast.error(`POD nem található: ${podId}`);
        return;
      }

      selectedPodsData.push({
        id: selectedPod.id,
        address: selectedPod.address,
        measurementType: selectedPod.measurementType,
        annualConsumption: selectedPod.annualConsumption
      });
    }

    const updateData = {
      customerId: customer.id,
      customerName: customer.type === 'organization' ? customer.companyName : `${customer.firstName} ${customer.lastName}`,
      customerType: customer.type,
      pods: selectedPodsData,
      contractType: data.contractType,
      provider: data.provider,
      tariffType: data.tariffType,
      status: data.status,
      startDate: data.startDate,
      endDate: data.endDate,
      pricePerKwh: data.pricePerKwh,
      monthlyFee: data.monthlyFee,
      pricePackage: data.pricePackage,
      notes: data.notes
    };
    
    try {
      await updateContract(selectedContract.id, updateData);
      setSelectedContract(null);
      setShowContractForm(false);
      toast.success('Szerződés sikeresen frissítve!');
    } catch (err) {
      toast.error('Hiba a szerződés módosítása során!');
    }
  };

  // Szerződés törlése
  const handleDeleteContract = async (id: string) => {
    if (window.confirm('Biztosan törölni szeretné ezt a szerződést?')) {
      try {
        await deleteContract(id);
        toast.success('Szerződés törölve!');
      } catch (err) {
        toast.error('Hiba a szerződés törlése során!');
      }
    }
  };

  // Tömeges törlés
  const handleBulkDelete = async () => {
    if (window.confirm(`Biztosan törölni szeretné a kiválasztott ${selectedContracts.length} szerződést?`)) {
      try {
        // Minden kiválasztott szerződés törlése
        await Promise.all(selectedContracts.map(id => deleteContract(id)));
        setSelectedContracts([]);
        toast.success('Kiválasztott szerződések törölve!');
      } catch (err) {
        toast.error('Hiba történt a szerződések törlése során!');
      }
    }
  };

  // Kijelölés kezelése
  const handleSelectContract = (id: string) => {
    setSelectedContracts(prev => 
      prev.includes(id) 
        ? prev.filter(contractId => contractId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedContracts.length === currentContracts.length) {
      setSelectedContracts([]);
    } else {
      setSelectedContracts(currentContracts.map(contract => contract.id));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <CommonPageHeader
        title="Energia Szerződések"
        subtitle="Energia szerződések kezelése meglévő ügyfelek POD adatai alapján"
        icon={BuildingOffice2Icon}
        showLastUpdate={true}
      >
        <button
          onClick={() => setShowContractForm(true)}
          className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Új Szerződés
        </button>
      </CommonPageHeader>

      {/* Keresés és szűrők */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Keresés */}
            <div className="flex-1">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Keresés szerződés szám, ügyfél név vagy POD szerint..."
              />
            </div>

            {/* Szűrők gomb */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <FunnelIcon className="w-5 h-5 mr-2" />
              Szűrők
              {showFilters && <span className="ml-2 text-blue-600">●</span>}
            </button>
          </div>

          {/* Szűrők panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ügyfél</label>
                  <select
                    value={customerFilter}
                    onChange={(e) => setCustomerFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  >
                    <option value="">Összes ügyfél</option>
                    {customersWithPods.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.type === 'organization' ? customer.companyName : `${customer.firstName} ${customer.lastName}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Státusz</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  >
                    <option value="">Összes státusz</option>
                    <option value="active">Aktív</option>
                    <option value="pending">Függőben</option>
                    <option value="expired">Lejárt</option>
                    <option value="terminated">Felmondva</option>
                    <option value="draft">Piszkozat</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Szolgáltató</label>
                  <select
                    value={providerFilter}
                    onChange={(e) => setProviderFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  >
                    <option value="">Összes szolgáltató</option>
                    <option value="EON">EON</option>
                    <option value="ELMU">ELMU</option>
                    <option value="DEMASZ">DEMASZ</option>
                    <option value="OPUS">OPUS</option>
                    <option value="MVMI">MVMI</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Szerződés típus</label>
                  <select
                    value={contractTypeFilter}
                    onChange={(e) => setContractTypeFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  >
                    <option value="">Összes típus</option>
                    <option value="new">Új szerződés</option>
                    <option value="live">Élő szerződés</option>
                    <option value="modification">Módosítás</option>
                    <option value="renewal">Megújítás</option>
                    <option value="termination">Felmondás</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tarifa típus</label>
                  <select
                    value={tariffTypeFilter}
                    onChange={(e) => setTariffTypeFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  >
                    <option value="">Összes tarifa</option>
                    <option value="residential">Lakossági</option>
                    <option value="commercial">Kereskedelmi</option>
                    <option value="industrial">Ipari</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Összesítő kártyák */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <BuildingOffice2Icon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Összes szerződés</p>
              <p className="text-2xl font-semibold text-gray-900">{filteredContracts.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
              <div className="h-4 w-4 bg-green-600 rounded-full"></div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Aktív szerződések</p>
              <p className="text-2xl font-semibold text-gray-900">
                {filteredContracts.filter(c => c.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <UserIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">POD-okkal rendelkező ügyfelek</p>
              <p className="text-2xl font-semibold text-gray-900">{customersWithPods.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
              <div className="h-4 w-4 bg-yellow-600 rounded-full"></div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Függőben lévő</p>
              <p className="text-2xl font-semibold text-gray-900">
                {filteredContracts.filter(c => c.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading és error állapot */}
      {loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Szerződések betöltése...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center text-red-600">
            Hiba történt a szerződések betöltése során: {error.message}
          </div>
        </div>
      )}

      {/* Szerződések táblázat */}
      {!loading && !error && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Szerződések ({filteredContracts.length})
            </h2>
            
            {selectedContracts.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedContracts.length} kiválasztva
                </span>
                <button
                  onClick={handleBulkDelete}
                  className="inline-flex items-center px-3 py-1 border border-red-300 text-red-700 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                >
                  <TrashIcon className="w-4 h-4 mr-1" />
                  Törlés
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedContracts.length === currentContracts.length && currentContracts.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Szerződés
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ügyfél
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  POD
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Szolgáltató
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Státusz
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Időtartam
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Árazás
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentContracts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-2">Nincsenek szerződések</p>
                    <p className="text-sm text-gray-400 mb-4">
                      {customersWithPods.length > 0 
                        ? `${customersWithPods.length} ügyfélnek van POD adata, akiknek létrehozhat szerződést`
                        : 'Először adjon hozzá ügyfeleket POD adatokkal'
                      }
                    </p>
                    <button
                      onClick={() => setShowContractForm(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      disabled={customersWithPods.length === 0}
                    >
                      <PlusIcon className="w-4 h-4 mr-2" />
                      Első szerződés létrehozása
                    </button>
                  </td>
                </tr>
              ) : (
                currentContracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedContracts.includes(contract.id)}
                        onChange={() => handleSelectContract(contract.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {contract.contractNumber}
                      </div>
                      <div className="text-sm text-gray-500">
                        {contract.contractType === 'new' ? 'Új' : 
                         contract.contractType === 'modification' ? 'Módosítás' :
                         contract.contractType === 'renewal' ? 'Megújítás' : 
                         contract.contractType === 'live' ? 'Élő szerződés' : 'Felmondás'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          {contract.customerType === 'organization' ? (
                            <BuildingOffice2Icon className="h-8 w-8 text-gray-400" />
                          ) : (
                            <UserIcon className="h-8 w-8 text-gray-400" />
                          )}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {contract.customerName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {contract.customerType === 'organization' ? 'Szervezet' : 'Magánszemély'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {contract.pods.length === 1 ? (
                        // Egyetlen POD esetén
                        <div>
                          <div className="text-sm text-gray-900 font-mono">
                            {contract.pods[0].id}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-32">
                            {contract.pods[0].address}
                          </div>
                        </div>
                      ) : contract.pods.length <= 3 ? (
                        // 2-3 POD esetén
                        <div className="space-y-1">
                          {contract.pods.map((pod) => (
                            <div key={pod.id}>
                              <div className="text-xs text-gray-900 font-mono truncate">
                                {pod.id}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Sok POD esetén (4+)
                        <div>
                          <div className="text-sm font-medium text-gray-900 mb-1">
                            📍 {contract.pods.length} POD
                          </div>
                          <div className="space-y-0.5">
                            {contract.pods.slice(0, 2).map((pod) => (
                              <div key={pod.id} className="text-xs text-gray-600 font-mono truncate">
                                {pod.id}
                              </div>
                            ))}
                            <div className="text-xs text-gray-400">
                              + {contract.pods.length - 2} további POD
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProviderColor(contract.provider)}`}>
                        {contract.provider}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                        {getStatusText(contract.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{contract.startDate}</div>
                      <div className="text-gray-500">{contract.endDate}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{contract.monthlyFee.toLocaleString()} Ft/hó</div>
                      <div className="text-gray-500">{contract.pricePerKwh} Ft/kWh</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {/* Részletek megtekintése */}}
                          className="text-blue-600 hover:text-blue-900"
                          title="Részletek"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedContract(contract);
                            setShowContractForm(true);
                          }}
                          className="text-green-600 hover:text-green-900"
                          title="Szerkesztés"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteContract(contract.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Törlés"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Előző
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Következő
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">{indexOfFirstItem + 1}</span>
                  {' '}-{' '}
                  <span className="font-medium">{Math.min(indexOfLastItem, filteredContracts.length)}</span>
                  {' '}közül{' '}
                  <span className="font-medium">{filteredContracts.length}</span>
                  {' '}találat
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Előző
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Következő
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Szerződés form modal */}
      <EnergyContractForm
        isOpen={showContractForm}
        onCancel={() => {
          setShowContractForm(false);
          setSelectedContract(null);
        }}
        onSubmit={selectedContract ? handleEditContract : handleCreateContract}
        initialData={selectedContract}
        customers={customersWithPods}
      />
    </div>
  );
};

// Energia szerződés form komponens
interface EnergyContractFormProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (data: EnergyContractFormData) => void;
  initialData?: EnergyContract | null;
  customers: Customer[];
}

const EnergyContractForm: React.FC<EnergyContractFormProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  initialData,
  customers
}) => {
  const { t } = useLanguage();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [availablePods, setAvailablePods] = useState<Array<{id: string, address: string, type: 'main' | 'additional', index?: number}>>([]);
  const [customerQuery, setCustomerQuery] = useState('');

  const defaultValues: EnergyContractFormData = {
    customerId: '',
    selectedPods: [],
    contractNumber: '',
    contractType: 'new',
    provider: 'EON',
    tariffType: 'residential',
    status: 'draft',
    startDate: '',
    endDate: '',
    currency: 'HUF',
    pricePerKwh: 0,
    monthlyFee: 0,
    pricePackage: '',
    notes: ''
  };

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<EnergyContractFormData>({
    defaultValues: initialData ? {
      customerId: initialData.customerId,
      selectedPods: initialData.pods.map(pod => pod.id),
      contractNumber: initialData.contractNumber,
      contractType: initialData.contractType,
      provider: initialData.provider,
      tariffType: initialData.tariffType,
      status: initialData.status,
      startDate: initialData.startDate,
      endDate: initialData.endDate,
      currency: initialData.currency as 'HUF' | 'EUR',
      pricePerKwh: initialData.pricePerKwh,
      monthlyFee: initialData.monthlyFee,
      pricePackage: initialData.pricePackage,
      notes: initialData.notes
    } : defaultValues
  });

  const watchedCustomerId = watch('customerId');
  const watchedSelectedPods = watch('selectedPods');

  // Szűrt ügyfelek a keresési kifejezés alapján
  const filteredCustomers = React.useMemo(() => {
    return customers.filter(customer => {
      const searchStr = customer.type === 'organization'
        ? customer.companyName?.toLowerCase()
        : `${customer.firstName} ${customer.lastName}`.toLowerCase();
      
      const searchTermLower = customerQuery.toLowerCase();
      
      return searchStr?.includes(searchTermLower) ||
        // Email keresés
        customer.email?.toLowerCase().includes(searchTermLower) ||
        // Adószám és cégjegyzékszám keresés
        customer.taxNumber?.toLowerCase().includes(searchTermLower) ||
        customer.registrationNumber?.toLowerCase().includes(searchTermLower) ||
        // POD keresés
        customer.pod?.id?.toLowerCase().includes(searchTermLower) ||
        // Cím elemek keresése
        customer.address?.street?.toLowerCase().includes(searchTermLower) ||
        customer.address?.city?.toLowerCase().includes(searchTermLower) ||
        customer.address?.postalCode?.toLowerCase().includes(searchTermLower) ||
        customer.address?.country?.toLowerCase().includes(searchTermLower) ||
        // Teljes cím keresése (ha van)
        customer.companyAddress?.toLowerCase().includes(searchTermLower);
    });
  }, [customers, customerQuery]);

  // Ügyfél kiválasztása
  const handleCustomerSelect = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    setValue('customerId', customerId);
    setSelectedCustomer(customer || null);
  };

  // Ügyfél változásakor frissítjük a POD opciókat
  React.useEffect(() => {
    if (watchedCustomerId) {
      const customer = customers.find(c => c.id === watchedCustomerId);
      setSelectedCustomer(customer || null);
      
      if (customer) {
        const pods = [];
        
        // Fő POD
        if (customer.pod?.id) {
          pods.push({
            id: customer.pod.id,
            address: customer.pod.address,
            type: 'main' as const
          });
        }
        
        // További POD-ok
        if (customer.additionalPods) {
          customer.additionalPods.forEach((pod, index) => {
            pods.push({
              id: pod.id,
              address: pod.address,
              type: 'additional' as const,
              index
            });
          });
        }
        
        setAvailablePods(pods);
        
        // Alapértékek beállítása az ügyfél adataiból
        // Valuta beállítása
        let currency = 'HUF';
        if (customer.currentPricing?.currency) {
          currency = customer.currentPricing.currency;
        } else if (customer.offerPrice?.currency) {
          currency = customer.offerPrice.currency;
        }
        setValue('currency', currency as 'HUF' | 'EUR');
        
        // Árazási adatok - először currentPricing, aztán offerPrice
        if (customer.currentPricing?.price) {
          const priceMatch = customer.currentPricing.price.match(/(\d+(?:\.\d+)?)/);
          if (priceMatch) {
            setValue('pricePerKwh', parseFloat(priceMatch[1]));
          }
        } else if (customer.offerPrice?.price) {
          const priceMatch = customer.offerPrice.price.match(/(\d+(?:\.\d+)?)/);
          if (priceMatch) {
            setValue('pricePerKwh', parseFloat(priceMatch[1]));
          }
        }
        
        // Szerződés időtartam
        if (customer.contractPeriod?.startDate) {
          setValue('startDate', customer.contractPeriod.startDate);
        }
        
        if (customer.contractPeriod?.endDate) {
          setValue('endDate', customer.contractPeriod.endDate);
        }
        
        // Havi alapdíj becslés (ha nincs külön mezője, akkor 2500 Ft alapértelmezett)
        setValue('monthlyFee', 2500);
        
        // Árcsomag beállítása az ügyfél adatai alapján
        let pricePackage = '';
        if (customer.currentPricing?.fix_tozsde) {
          pricePackage = customer.currentPricing.fix_tozsde;
        } else if (customer.csvSourceData?.fixTozsde) {
          pricePackage = customer.csvSourceData.fixTozsde;
        }
        
        if (pricePackage) {
          setValue('pricePackage', pricePackage);
        }
      }
    } else {
      setSelectedCustomer(null);
      setAvailablePods([]);
    }
  }, [watchedCustomerId, customers, setValue]);

  React.useEffect(() => {
    if (isOpen && !initialData) {
      reset(defaultValues);
      setSelectedCustomer(null);
      setAvailablePods([]);
      setCustomerQuery('');
    } else if (isOpen && initialData) {
      // Meglévő szerződés szerkesztésekor beállítjuk az ügyfelet
      const customer = customers.find(c => c.id === initialData.customerId);
      if (customer) {
        setSelectedCustomer(customer);
      }
    }
  }, [isOpen, initialData, reset, customers]);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onCancel} 
      title={initialData ? 'Szerződés szerkesztése' : 'Új energia szerződés'}
      size="6xl"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
        {/* Szerződésszám szekció */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentTextIcon className="w-5 h-5 text-green-600" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 mb-4">Szerződés azonosítás</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Szerződésszám (opcionális)
                </label>
                <input
                  type="text"
                  {...register('contractNumber')}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                           placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Pl.: EI-0001-S-202501-ABCD vagy hagyja üresen automatikus generáláshoz"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Ha üres, automatikusan generálódik: EI-[ügyfélkód]-S-[év/hónap]-[véletlenszerű]
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Ügyfél kiválasztás szekció */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <UserIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">Ügyfél és POD kiválasztás</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ügyfél *
                  </label>
                  <Combobox
                    value={watch('customerId')}
                    onChange={(customerId) => handleCustomerSelect(customerId)}
                  >
                    <div className="relative">
                      <div className="relative w-full">
                        <Combobox.Input
                          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                                   placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          onChange={event => setCustomerQuery(event.target.value)}
                          onClick={() => {
                            // Üres keresésre minden ügyfelet mutassunk
                            setCustomerQuery('');
                          }}
                          displayValue={(customerId: string) => {
                            const customer = customers.find(c => c.id === customerId);
                            if (!customer) return '';
                            return customer.type === 'organization'
                              ? customer.companyName || ''
                              : `${customer.firstName} ${customer.lastName}`;
                          }}
                          placeholder="Keressen ügyfelet név, kód, email vagy POD alapján..."
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon
                            className="h-5 w-5 text-gray-500"
                            aria-hidden="true"
                          />
                        </Combobox.Button>
                      </div>
                      <Combobox.Options
                        className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg 
                                 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                      >
                        {filteredCustomers.length === 0 && customerQuery !== '' ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                            Nincs találat a keresési feltételeknek megfelelően
                          </div>
                        ) : (
                          filteredCustomers.map(customer => (
                            <Combobox.Option
                              key={customer.id}
                              value={customer.id}
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-primary-100 text-primary-900' : 'text-gray-800'
                                }`
                              }
                            >
                              {({ selected, active }) => (
                                <div className="flex items-center space-x-3">
                                  <div className={`p-1 rounded ${customer.type === 'organization'
                                    ? 'bg-blue-100'
                                    : 'bg-purple-100'
                                    }`}>
                                    {customer.type === 'organization' ? (
                                      <BuildingOffice2Icon className="h-4 w-4 text-blue-600" />
                                    ) : (
                                      <UserIcon className="h-4 w-4 text-purple-600" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium">
                                      {customer.type === 'organization'
                                        ? customer.companyName
                                        : `${customer.firstName} ${customer.lastName}`}
                                    </div>
                                    <div className="text-xs text-gray-500 flex items-center space-x-2">
                                      <span>{customer.email}</span>
                                      {customer.pod?.id && (
                                        <span className="text-blue-600 bg-blue-50 px-1 py-0.5 rounded text-xs">
                                          {customer.pod.id}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Combobox.Option>
                          ))
                        )}
                      </Combobox.Options>
                    </div>
                  </Combobox>

                  {/* Hidden input a customerId tárolásához */}
                  <input
                    type="hidden"
                    {...register('customerId', { required: 'Ügyfél kiválasztása kötelező' })}
                  />

                  {errors.customerId && (
                    <p className="mt-1 text-sm text-red-600">{errors.customerId.message}</p>
                  )}
                </div>

                {availablePods.length > 0 && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      POD kiválasztás * (több POD is kiválasztható)
                    </label>
                    <div className="space-y-2">
                      {availablePods.map((pod, index) => {
                        const isChecked = watchedSelectedPods?.includes(pod.id) || false;
                        return (
                          <label
                            key={index}
                            className="relative flex items-center group"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const currentPods = watchedSelectedPods || [];
                                if (e.target.checked) {
                                  setValue('selectedPods', [...currentPods, pod.id]);
                                } else {
                                  setValue('selectedPods', currentPods.filter(id => id !== pod.id));
                                }
                              }}
                              className="sr-only peer"
                            />
                            <div className={`flex items-center w-full px-3 py-2 rounded-lg border transition-all duration-200 cursor-pointer ${
                              isChecked 
                                ? 'border-primary-500 bg-primary-50' 
                                : 'border-gray-300 hover:border-gray-400'
                            }`}>
                              <div className={`flex items-center justify-center w-5 h-5 mr-3 rounded border transition-all duration-200 ${
                                isChecked 
                                  ? 'bg-primary-600 border-primary-600' 
                                  : 'border-gray-300 bg-white'
                              }`}>
                                {isChecked && (
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1">
                                <div className={`text-sm font-medium transition-colors duration-200 ${
                                  isChecked ? 'text-primary-700' : 'text-gray-900'
                                }`}>
                                  {pod.id}
                                </div>
                                <div className={`text-xs transition-colors duration-200 ${
                                  isChecked ? 'text-primary-600' : 'text-gray-600'
                                }`}>
                                  {pod.address} ({pod.type === 'main' ? 'Fő POD' : 'További POD'})
                                </div>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {watchedSelectedPods?.length === 0 && (
                      <p className="mt-1 text-sm text-red-600">Legalább egy POD kiválasztása kötelező</p>
                    )}
                  </div>
                )}
              </div>

              {selectedCustomer && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-blue-800 mb-3">Ügyfél információk</h4>
                  
                  {/* Alapadatok */}
                  <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                    <div>
                      <span className="font-medium">Típus:</span> {selectedCustomer.type === 'organization' ? 'Szervezet' : 'Magánszemély'}
                    </div>
                    <div>
                      <span className="font-medium">Email:</span> {selectedCustomer.email}
                    </div>
                    <div>
                      <span className="font-medium">Telefon:</span> {selectedCustomer.phone}
                    </div>
                    <div>
                      <span className="font-medium">Adószám:</span> {selectedCustomer.taxNumber}
                    </div>
                  </div>

                  {/* Árazási információk */}
                  {(selectedCustomer.currentPricing || selectedCustomer.offerPrice) && (
                    <div className="border-t border-blue-200 pt-3">
                      <h5 className="text-xs font-medium text-blue-700 mb-2">💰 Árazási adatok</h5>
                      <div className="grid grid-cols-1 gap-2 text-xs">
                        {selectedCustomer.currentPricing && (
                          <div className="bg-white bg-opacity-50 rounded p-2">
                            <div className="flex justify-between items-center">
                              <span className="font-medium">Jelenlegi ár ({selectedCustomer.currentPricing.year}):</span>
                              <span className="text-blue-800 font-semibold">
                                {selectedCustomer.currentPricing.price} {selectedCustomer.currentPricing.currency}
                              </span>
                            </div>
                            {selectedCustomer.currentPricing.fix_tozsde && (
                              <div className="text-blue-600 mt-1">
                                Típus: {selectedCustomer.currentPricing.fix_tozsde}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {selectedCustomer.offerPrice && !selectedCustomer.currentPricing && (
                          <div className="bg-white bg-opacity-50 rounded p-2">
                            <div className="flex justify-between items-center">
                              <span className="font-medium">Ajánlati ár:</span>
                              <span className="text-blue-800 font-semibold">
                                {selectedCustomer.offerPrice.price} {selectedCustomer.offerPrice.currency}
                              </span>
                            </div>
                          </div>
                        )}

                        {selectedCustomer.futurePricing && selectedCustomer.futurePricing.length > 0 && (
                          <div className="text-blue-600">
                            🔮 {selectedCustomer.futurePricing.length} jövőbeli ár elérhető
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* POD információk */}
                  <div className="border-t border-blue-200 pt-3 mt-3">
                    <h5 className="text-xs font-medium text-blue-700 mb-2">⚡ POD adatok</h5>
                    <div className="text-xs space-y-1">
                      {selectedCustomer.pod && (
                        <div className="bg-white bg-opacity-50 rounded p-2">
                          <div className="font-medium">Fő POD: {selectedCustomer.pod.id}</div>
                          <div className="text-blue-600">{selectedCustomer.pod.address}</div>
                          <div className="text-blue-600">Éves fogyasztás: {selectedCustomer.pod.annualConsumption}</div>
                        </div>
                      )}
                      {selectedCustomer.additionalPods && selectedCustomer.additionalPods.length > 0 && (
                        <div className="text-blue-600">
                          + {selectedCustomer.additionalPods.length} további POD
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Szerződés alapadatok szekció */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <BuildingOffice2Icon className="w-5 h-5 text-green-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">Szerződés alapadatok</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Szerződés típusa *
                  </label>
                  <select
                    {...register('contractType', { required: 'Szerződés típus kötelező' })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="new">Új szerződés</option>
                    <option value="live">Élő szerződés</option>
                    <option value="modification">Módosítás</option>
                    <option value="renewal">Megújítás</option>
                    <option value="termination">Felmondás</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Szolgáltató *
                  </label>
                  <select
                    {...register('provider', { required: 'Szolgáltató kötelező' })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="EON">EON - E.ON Földgáz Trade Zrt.</option>
                    <option value="ELMU">ELMU - ELMŰ Hálózati Kft.</option>
                    <option value="DEMASZ">DEMASZ - DÉMÁSZ Hálózati Kft.</option>
                    <option value="OPUS">OPUS - OPUS TITÁSZ Hálózati Kft.</option>
                    <option value="MVMI">MVMI - MVM Főgáz Hálózati Kft.</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tarifa típus *
                  </label>
                  <select
                    {...register('tariffType', { required: 'Tarifa típus kötelező' })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="residential">Lakossági</option>
                    <option value="commercial">Kereskedelmi</option>
                    <option value="industrial">Ipari</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Státusz *
                  </label>
                  <select
                    {...register('status', { required: 'Státusz kötelező' })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="draft">Piszkozat</option>
                    <option value="active">Aktív</option>
                    <option value="pending">Függőben</option>
                    <option value="expired">Lejárt</option>
                    <option value="terminated">Felmondva</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Időtartam és árazás szekció */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-purple-100 to-fuchsia-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <CalendarDaysIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">Időtartam és árazás</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Szerződés kezdete *
                  </label>
                  <input
                    type="date"
                    {...register('startDate', { required: 'Kezdő dátum kötelező' })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                  {errors.startDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Szerződés vége *
                  </label>
                  <input
                    type="date"
                    {...register('endDate', { required: 'Befejező dátum kötelező' })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                  {errors.endDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.endDate.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valuta *
                  </label>
                  <select
                    {...register('currency', { required: 'Valuta kötelező' })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="HUF">HUF - Magyar forint</option>
                    <option value="EUR">EUR - Euro</option>
                  </select>
                  {errors.currency && (
                    <p className="mt-1 text-sm text-red-600">{errors.currency.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Egységár ({watch('currency') || 'HUF'}/kWh) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('pricePerKwh', { 
                      required: 'Egységár kötelező',
                      min: { value: 0, message: 'Egységár nem lehet negatív' }
                    })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="45.50"
                  />
                  {errors.pricePerKwh && (
                    <p className="mt-1 text-sm text-red-600">{errors.pricePerKwh.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Havi alapdíj ({watch('currency') || 'HUF'}) *
                  </label>
                  <input
                    type="number"
                    {...register('monthlyFee', { 
                      required: 'Havi alapdíj kötelező',
                      min: { value: 0, message: 'Havi alapdíj nem lehet negatív' }
                    })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="2500"
                  />
                  {errors.monthlyFee && (
                    <p className="mt-1 text-sm text-red-600">{errors.monthlyFee.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Árcsomag
                  </label>
                  <input
                    type="text"
                    {...register('pricePackage')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="Alapcsomag, Zöld csomag, Üzleti csomag..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Megjegyzések szekció */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-gray-100 to-slate-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentTextIcon className="w-5 h-5 text-gray-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">Megjegyzések</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  További információk
                </label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                           placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Szerződéssel kapcsolatos megjegyzések, különleges feltételek..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-xs font-medium text-gray-700 hover:text-gray-900"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={!watchedCustomerId || watchedSelectedPods?.length === 0}
            className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {initialData ? 'Szerződés frissítése' : 'Szerződés létrehozása'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EnergiaContracts; 